// useConversationCache.ts
import { useState, useEffect } from 'react';
import { Message, CachedConversation } from '../types/message';

const CACHE_EXPIRY = 1000 * 60 * 60 * 8; // 8 hours

export const useConversationCache = (userId: string, disabled: boolean = false) => {
  const [conversationCache, setConversationCache] = useState<Record<string, CachedConversation>>(() => {
    // If cache is disabled, don't even try to load from localStorage
    if (disabled) return {};
    
    const cached = localStorage.getItem(`conversations_${userId}`);
    if (!cached) return {};
    
    try {
      const parsedCache = JSON.parse(cached);
      const now = Date.now();
      
      // Filter out expired or invalid entries
      const validEntries = Object.entries(parsedCache).reduce((acc, [key, value]: [string, any]) => {
        // Check if cache is expired
        if (now - value.timestamp > CACHE_EXPIRY) return acc;
        
        // Check if conversation has all required properties
        if (!(value.id && 
              value.userId && 
              value.title !== undefined && 
              value.lastMessageAt !== undefined && 
              value.createdAt !== undefined)) {
          return acc;
        }
        
        // Entry is valid, keep it
        return { ...acc, [key]: value };
      }, {});
      
      return validEntries;
    } catch (error) {
      console.error('Error parsing conversation cache:', error);
      return {};
    }
  });

  // Save to localStorage whenever cache changes
  useEffect(() => {
    // Skip saving to localStorage if cache is disabled
    if (disabled) return;
    
    if (userId) {  // Only save if we have a userId
      localStorage.setItem(`conversations_${userId}`, JSON.stringify(conversationCache));
    }
  }, [conversationCache, userId, disabled]);

  const getCachedConversation = (conversationId: string) => {
    // Return null immediately if cache is disabled
    if (disabled) return null;
    
    const cached = conversationCache[conversationId];
    if (!cached) return null;

    // Check if cache is expired
    if (Date.now() - cached.timestamp > CACHE_EXPIRY) {
      // Remove expired cache
      const { [conversationId]: _, ...rest } = conversationCache;
      setConversationCache(rest);
      return null;
    }

    // Check if conversation has all required properties
    if (!(cached.id && 
          cached.userId && 
          cached.title !== undefined && 
          cached.lastMessageAt !== undefined && 
          cached.createdAt !== undefined)) {
      // Remove invalid cache
      const { [conversationId]: _, ...rest } = conversationCache;
      setConversationCache(rest);
      return null;
    }

    return cached;
  };

  const getCachedConversations = () => {
    // Return null immediately if cache is disabled
    if (disabled) return null;
    
    const now = Date.now();
    const validCacheEntries = Object.entries(conversationCache).filter(
      ([_, value]) => {
        // Check if cache is expired
        if (now - value.timestamp > CACHE_EXPIRY) return false;
        
        // Check if conversation has all required properties
        return (
          value.id && 
          value.userId && 
          value.title !== undefined && 
          value.lastMessageAt !== undefined && 
          value.createdAt !== undefined
        );
      }
    );

    if (validCacheEntries.length === 0) return null;

    return validCacheEntries
      .map(([_, value]) => value)
      .sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  };

  const cacheConversation = (conversationId: string, data: any) => {
    // Do nothing if cache is disabled
    if (disabled) return;
    
    setConversationCache(prev => {
      const existingCache = prev[conversationId];
      return {
        ...prev,
        [conversationId]: {
          id: conversationId,
          userId: data.userId || userId, // Use the userId from the hook if not provided
          title: data.title || existingCache?.title || 'New Conversation',
          messages: data.messages || existingCache?.messages || [],
          timestamp: Date.now(),
          lastMessageAt: data.lastMessageAt || existingCache?.lastMessageAt || Date.now(),
          createdAt: data.createdAt || existingCache?.createdAt || Date.now()
        }
      };
    });
  };

  const cacheConversations = (conversations: any[]) => {
    // Do nothing if cache is disabled
    if (disabled) return;
    
    setConversationCache(prev => {
      const newCache = conversations.reduce((acc, conv) => {
        // Preserve existing messages if we have them
        const existing = prev[conv.id];
        return {
          ...acc,
          [conv.id]: {
            id: conv.id,
            userId: conv.userId || userId, // Use the userId from the hook if not provided
            title: conv.title || 'New Conversation',
            messages: existing?.messages || [],
            timestamp: Date.now(),
            lastMessageAt: conv.lastMessageAt || Date.now(),
            createdAt: conv.createdAt || Date.now()
          }
        };
      }, {});

      return newCache;
    });
  };

  const invalidateCache = (conversationId?: string) => {
    if (conversationId) {
      setConversationCache(prev => {
        const { [conversationId]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setConversationCache({});
    }
  };

  return {
    getCachedConversation,
    getCachedConversations,
    cacheConversation,
    cacheConversations,
    invalidateCache
  };
};

export default useConversationCache;
